import { Component, OnDestroy, Renderer2, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { LayoutService } from '../../services/layout.service';

@Component({
    standalone: false,
    selector: 'app-admin-shell',
    templateUrl: './admin-shell.component.html',
})
export class AdminShellComponent implements OnDestroy {
    protected readonly layoutService = inject(LayoutService);
    private readonly renderer = inject(Renderer2);
    private readonly router = inject(Router);

    private readonly subscriptions: Subscription[] = [];
    private menuOutsideClickListener: (() => void) | null = null;

    constructor() {
        this.subscriptions.push(
            this.layoutService.overlayOpen$.subscribe(() => {
                if (this.menuOutsideClickListener) return;
                this.menuOutsideClickListener = this.renderer.listen(
                    'document',
                    'click',
                    (event: MouseEvent) => {
                        if (this.isOutsideClicked(event)) this.hideMenu();
                    },
                );
            }),

            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe(() => this.hideMenu()),
        );
    }

    protected get containerClass(): Record<string, boolean> {
        const config = this.layoutService.layoutConfig();
        const state = this.layoutService.layoutState();
        return {
            'layout-overlay': config.menuMode === 'overlay',
            'layout-static': config.menuMode === 'static',
            'layout-static-inactive':
                state.staticMenuDesktopInactive && config.menuMode === 'static',
            'layout-overlay-active': state.overlayMenuActive,
            'layout-mobile-active': state.staticMenuMobileActive,
        };
    }

    private isOutsideClicked(event: MouseEvent): boolean {
        const sidebar = document.querySelector('.layout-sidebar');
        const menuButton = document.querySelector('.layout-menu-button');
        const target = event.target as Node;

        return !(
            sidebar?.isSameNode(target) ||
            sidebar?.contains(target) ||
            menuButton?.isSameNode(target) ||
            menuButton?.contains(target)
        );
    }

    private hideMenu(): void {
        this.layoutService.hideMenu();
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.menuOutsideClickListener?.();
    }
}
